/**
 * Helper to process and store Auth0 user data in localStorage
 */
import { StorageService } from '@/helpers/storage'

export async function processAndStoreUserData(auth0Instance) {
  if (!auth0Instance || !auth0Instance.isAuthenticated || !auth0Instance.user) {
    return null
  }

  try {
    // Get access token with correct audience - but don't store it
    const getToken =
      auth0Instance.getTokenSilently || auth0Instance.getAccessTokenSilently
    let accessToken
    try {
      accessToken = await getToken.call(auth0Instance, {
        authorizationParams: {
          audience:
            process.env.VUE_APP_AUTH0_AUDIENCE || 'https://myapi.inventory/'
        },
        ignoreCache: false, // Use cache to avoid unnecessary API calls
        cacheMode: 'memory' // Use memory cache instead of localStorage
      })
    } catch (tokenError) {
      console.warn('Could not get access token:', tokenError)
      // Continue without token-based claims
    }

    const user = auth0Instance.user
    let userRoles = []
    let orgId = []

    // Extract roles from various Auth0 user sources
    if (user.roles) userRoles.push(...user.roles)
    if (user.app_metadata?.roles) userRoles.push(...user.app_metadata.roles)
    if (user['https://inventory.yourcompany.com/roles'])
      userRoles.push(...user['https://inventory.yourcompany.com/roles'])
    if (user['https://inventory.yourcompany.com/org_id'])
      orgId = user['https://inventory.yourcompany.com/org_id']
    if (user.app_metadata?.org_id) orgId = user.app_metadata.org_id

    // Decode and check ID token if available
    try {
      const idToken = await auth0Instance.getIdTokenClaims()
      if (idToken) {
        if (idToken.roles) userRoles.push(...idToken.roles)
        if (idToken['app_metadata']?.roles)
          userRoles.push(...idToken['app_metadata'].roles)
        if (idToken.org_id) orgId = idToken.org_id
        if (idToken['app_metadata']?.org_id)
          orgId = idToken['app_metadata'].org_id
      }
    } catch (e) {
      console.warn('Could not get ID token claims:', e)
    }

    // Decode access token claims if possible
    if (accessToken) {
      try {
        const tokenParts = accessToken.split('.')
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]))
          if (payload.roles) userRoles.push(...payload.roles)
          if (payload['app_metadata']?.roles)
            userRoles.push(...payload['app_metadata'].roles)
          if (payload.org_id) orgId = payload.org_id
          if (payload['app_metadata']?.org_id)
            orgId = payload['app_metadata'].org_id
        }
      } catch (e) {
        console.warn('Could not decode access token:', e)
      }
    }

    // Clean up roles
    userRoles = [...new Set(userRoles)].filter((role) => role && role.trim())
    if (userRoles.length === 0) userRoles = ['User']

    // Return complete user info (including email) for immediate use
    const userInfo = {
      name: user.name,
      email: user.email,
      given_name: user.given_name,
      family_name: user.family_name,
      nickname: user.nickname,
      picture: user.picture,
      sub: user.sub,
      email_verified: user.email_verified,
      roles: userRoles,
      org_id: orgId
    }

    // Store minimal data in localStorage (UI display only - no sensitive tokens)
    localStorage.setItem(
      'user_display_name',
      JSON.stringify(user.given_name || user.name || user.nickname || 'User')
    )
    localStorage.setItem('user_roles', JSON.stringify(userRoles))

    if (orgId && orgId.length > 0) {
      localStorage.setItem('user_org_id', JSON.stringify(orgId))
    }

    // Store user info but without sensitive data
    const safeUserInfo = {
      name: user.name,
      given_name: user.given_name,
      family_name: user.family_name,
      nickname: user.nickname,
      email_verified: user.email_verified,
      // Don't store email in localStorage - get it from Auth0 when needed
      sub: user.sub,
      roles: userRoles,
      org_id: orgId
    }
    localStorage.setItem('user_info', JSON.stringify(safeUserInfo))

    // Mark SPA as using SSO so route guards pass
    StorageService.saveUseSSO(true)

    // Small delay to ensure localStorage is written
    await new Promise((resolve) => setTimeout(resolve, 100))

    return userInfo
  } catch (error) {
    console.error('❌ Error processing user data:', error)
    return null
  }
}
